import React, { useState, useCallback } from "react";
import { ArrowLeft, Fingerprint, Server } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ServerProtocols from "../ServerProtocols";
import NvrArpTable from "./NvrArpTable";
import NvrAssetDetail from "./NvrAssetDetail";
import NvrCameraTable from "./NvrCameraTable";
import NvrInterfaces from "./NvrInterfaces";
import type { InterfaceEntry } from "./NvrPortVisualization";



const NvrPage: React.FC = () => {
    const navigate = useNavigate();

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const hostname = info.host_name || "NVR Asset";
    const client_id = info.client_id;

    const [fingerprintMode, setFingerprintMode] = useState(false);
    const [interfaces, setInterfaces] = useState<InterfaceEntry[]>([]);
    const [loadingInterfaces, setLoadingInterfaces] = useState(true);

    const handleInterfacesFetched = useCallback((data: InterfaceEntry[]) => {
        setInterfaces(prev => {
            // Only update if data changed (simple length check or deep equal)
            if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
            return data;
        });
        setLoadingInterfaces(false);
    }, []);

    return (
        <div className={`flex flex-col gap-4 p-4 bg-gray-900 text-slate-200 transition-all duration-500 ${fingerprintMode ? 'h-screen overflow-hidden pb-10' : 'min-h-screen'}`}>
            {/* Header Area */}
            <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap border-b border-slate-800 pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-600 rounded-lg shadow-lg shadow-orange-600/20">
                        <Server className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white tracking-tight leading-none mb-1">NVR Dashboard</h1>
                        <p className="text-[12px] text-slate-500 font-medium uppercase tracking-wider">Hostname : {hostname}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-slate-900/50 p-1 rounded-lg border border-slate-800/60 mr-2">
                        <div className={`p-1.5 rounded-md transition-colors ${fingerprintMode ? 'bg-orange-600 text-white' : 'text-slate-500'}`}>
                            <Fingerprint className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Fingerprint</span>
                        <button
                            onClick={() => setFingerprintMode(!fingerprintMode)}
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 focus:outline-none ${fingerprintMode ? 'bg-orange-600' : 'bg-slate-700'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${fingerprintMode ? 'translate-x-5.5' : 'translate-x-1'}`} />
                        </button>
                    </div>

                    <button
                        onClick={() => navigate(info.path)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-orange-700 rounded-lg text-xs font-bold text-white hover:bg-orange-500 transition-all active:scale-95 shadow-sm uppercase tracking-wide"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {fingerprintMode ? (
                <div className="flex-1 min-h-0 pb-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <ServerProtocols hideHeader={false} />
                </div>
            ) : (
                <div className="pb-20">
                    <div className="space-y-4">
                        <div >
                            <NvrAssetDetail
                                assetId={asset_id}
                                clientId={client_id}
                                onInterfacesFetched={handleInterfacesFetched}
                                interfaces={interfaces}
                            />
                        </div>
                        <div>
                            <NvrCameraTable assetId={asset_id} clientId={client_id} />
                        </div>
                        <div>
                            <NvrInterfaces
                                initialData={interfaces}
                                initialLoading={loadingInterfaces}
                            />
                        </div>

                        <div>
                            <NvrArpTable assetId={asset_id} clientId={client_id} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NvrPage;

